/**
 * Location Service
 * 
 * Handles geolocation functionality with proper permission checks and error handling.
 * Supports fetching current location with future extensibility for pin-based location selection.
 */

import logger from '../utils/logger';

/**
 * Error types for location operations
 */
export const LocationError = {
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  POSITION_UNAVAILABLE: 'POSITION_UNAVAILABLE',
  TIMEOUT: 'TIMEOUT',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
  UNKNOWN: 'UNKNOWN',
};

/**
 * Get user-friendly error messages for location errors
 * @param {string} errorType - The type of location error
 * @param {Function} t - Translation function from i18n context
 * @returns {Object} Object with title and message for the error
 */
export const getLocationErrorMessage = (errorType, t) => {
  switch (errorType) {
    case LocationError.PERMISSION_DENIED:
      return {
        title: t('ChatView.locationPermissionDeniedTitle'),
        message: t('ChatView.locationPermissionDeniedDesc'),
      };
    case LocationError.POSITION_UNAVAILABLE:
      return {
        title: t('ChatView.locationPositionUnavailableTitle'),
        message: t('ChatView.locationPositionUnavailableDesc'),
      };
    case LocationError.TIMEOUT:
      return {
        title: t('ChatView.locationTimeoutTitle'),
        message: t('ChatView.locationTimeoutDesc'),
      };
    case LocationError.NOT_SUPPORTED:
      return {
        title: t('ChatView.locationNotSupportedTitle'),
        message: t('ChatView.locationNotSupportedDesc'),
      };
    default:
      return {
        title: t('ChatView.locationErrorTitle'),
        message: t('ChatView.locationErrorDesc'),
      };
  }
};

/**
 * Check if geolocation is supported in the current browser
 * @returns {boolean} True if geolocation is supported
 */
export const isGeolocationSupported = () => {
  return 'geolocation' in navigator;
};

/**
 * Get the user's current location
 * @param {Object} options - Geolocation options
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {boolean} options.enableHighAccuracy - Request high accuracy (default: true)
 * @param {number} options.maximumAge - Maximum age of cached position (default: 0)
 * @returns {Promise<Object>} Promise that resolves with location data or rejects with error type
 */
export const getCurrentLocation = (options = {}) => {
  return new Promise((resolve, reject) => {
    // Check if geolocation is supported
    if (!isGeolocationSupported()) {
      logger.warn('Geolocation is not supported by this browser');
      reject(LocationError.NOT_SUPPORTED);
      return;
    }

    const defaultOptions = {
      timeout: 10000,
      enableHighAccuracy: true,
      maximumAge: 0,
    };

    const geolocationOptions = { ...defaultOptions, ...options };

    logger.debug('Requesting current location with options:', geolocationOptions);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: position.timestamp,
        };

        logger.debug('Location retrieved successfully:', {
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy,
        });

        resolve(locationData);
      },
      (error) => {
        let errorType;
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorType = LocationError.PERMISSION_DENIED;
            logger.warn('Location permission denied by user');
            break;
          case error.POSITION_UNAVAILABLE:
            errorType = LocationError.POSITION_UNAVAILABLE;
            logger.warn('Location position unavailable');
            break;
          case error.TIMEOUT:
            errorType = LocationError.TIMEOUT;
            logger.warn('Location request timed out');
            break;
          default:
            errorType = LocationError.UNKNOWN;
            logger.warn('Unknown location error:', error.message);
        }

        reject(errorType);
      },
      geolocationOptions,
    );
  });
};

/**
 * Create a geo URI from latitude and longitude
 * Format: geo:latitude,longitude (RFC 5870)
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {string} Geo URI string
 */
export const createGeoUri = (latitude, longitude) => {
  return `geo:${latitude},${longitude}`;
};

/**
 * Format location data for sharing
 * Future: Could be extended to support different formats or services
 * @param {Object} locationData - Location data from getCurrentLocation
 * @returns {string} Formatted location string
 */
export const formatLocationForSharing = (locationData) => {
  const { latitude, longitude } = locationData;
  // Return a Google Maps link (can be customized)
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

/**
 * Future: Get location by pin drop on map
 * Placeholder for future implementation
 * @returns {Promise<Object>} Promise that resolves with selected location data
 */
export const getLocationByPin = () => {
  return Promise.reject(new Error('Pin-based location selection not yet implemented'));
};

export default {
  getCurrentLocation,
  getLocationByPin,
  isGeolocationSupported,
  getLocationErrorMessage,
  formatLocationForSharing,
  createGeoUri,
  LocationError,
};
